import { searchPatch } from "@/lib/search";
import { errorWords } from "@alm/domain";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { RecordCard } from "@/components/app/lists/record-card";
import { EmptyRows } from "@/components/app/lists/empty-rows";
import { RecordRow } from "@/components/app/lists/record-row";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { RowActions } from "@/components/app/patterns/row-actions";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { orpc, orpcClient, type Output } from "@/lib/orpc";
import { useCanOrganizationWide } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import { RowsSkeleton } from "@/routes/_authenticated/_administration/settings/-components/rows-skeleton";
import { TherapyVendorForm } from "./-components/care/therapy-vendor-form";

export const Route = createFileRoute("/_authenticated/_administration/settings/therapy-vendors")({
  validateSearch: (search: Record<string, unknown>): { retired?: true } => ({
    retired: search.retired === true ? true : undefined,
  }),
  beforeLoad: ({ context }) => guardRoute(context.me, "therapy-vendors"),
  component: TherapyVendorsSettings,
});

type Vendor = Output<typeof orpcClient.care.therapy.vendors>[number];

const fail = (error: Error) => toast.error(errorWords(error));

const place = (vendor: Vendor) =>
  [vendor.city, vendor.state].filter(Boolean).join(", ") || "No address recorded";

/** The contract therapy companies whose clinicians work in the building. */
function TherapyVendorsSettings() {
  /* Vendors are contracted with the whole organization, so changing one is organization-wide. */
  const canManage = useCanOrganizationWide("therapy:manage");
  const queryClient = useQueryClient();
  const { retired: showRetired = false } = Route.useSearch();
  const patch = searchPatch(useNavigate({ from: Route.fullPath }));
  const setShowRetired = (next: boolean) => patch({ retired: next ? true : undefined });
  const [editing, setEditing] = useState<Vendor | null>(null);
  const [adding, setAdding] = useState(false);
  const [retiring, setRetiring] = useState<Vendor | null>(null);

  const vendors = useQuery(
    orpc.care.therapy.vendors.queryOptions({ input: { includeInactive: showRetired } }),
  );
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: orpc.care.therapy.vendors.key() });

  const save = useMutation(
    orpc.care.therapy.saveVendor.mutationOptions({
      onSuccess: async () => {
        toast.success("Vendor saved");
        setAdding(false);
        setEditing(null);
        await invalidate();
      },
      onError: fail,
    }),
  );
  const retire = useMutation(
    orpc.care.therapy.deactivateVendor.mutationOptions({
      onSuccess: async () => {
        toast.success("Vendor switched off");
        setRetiring(null);
        await invalidate();
      },
      onError: fail,
    }),
  );

  const rows = vendors.data ?? [];

  return (
    <>
      <PageHeader
        title="Therapy vendors"
        meta="The companies contracted to provide therapy, and the details that appear on a billed note."
      />

      <div className="flex items-center gap-2">
        <Switch checked={showRetired} id="show-retired" onCheckedChange={setShowRetired} />
        <Label htmlFor="show-retired">Show switched-off vendors</Label>
      </div>

      <RecordCard
        addLabel="Add vendor"
        canWrite={canManage}
        count={vendors.data ? rows.length : undefined}
        onAdd={() => setAdding(true)}
        title="Vendors"
      >
        {vendors.isPending ? (
          <RowsSkeleton />
        ) : vendors.isError ? (
          <ReadFailed
            message="The vendors could not be loaded."
            error={vendors.error}
            retrying={vendors.isFetching}
            onRetry={() => void vendors.refetch()}
          />
        ) : rows.length === 0 ? (
          <EmptyRows text="No therapy vendors yet. Add the company your therapists work for and it appears on their notes." />
        ) : (
          <ul className="divide-y">
            {rows.map((vendor) => (
              <RecordRow
                key={vendor.id}
                meta={
                  <>
                    {place(vendor)}
                    {vendor.npi ? `, NPI ${vendor.npi}` : ""}
                  </>
                }
                title={
                  <span className="flex items-center gap-2">
                    {vendor.legalName}
                    {vendor.dbaName ? (
                      <span className="text-muted-foreground font-normal">
                        trading as {vendor.dbaName}
                      </span>
                    ) : null}
                    {vendor.isActive ? null : <Badge variant="outline">Off</Badge>}
                  </span>
                }
                actions={
                  canManage ? (
                    <RowActions
                      modal={false}
                      label={vendor.legalName}
                      primary={{ label: "Edit", onSelect: () => setEditing(vendor) }}
                      items={[
                        vendor.isActive
                          ? { label: "Switch off", onSelect: () => setRetiring(vendor) }
                          : {
                              label: "Switch on",
                              onSelect: () =>
                                save.mutate({
                                  vendorId: vendor.id,
                                  legalName: vendor.legalName,
                                  isActive: true,
                                }),
                            },
                      ]}
                    />
                  ) : null
                }
              />
            ))}
          </ul>
        )}
      </RecordCard>

      <Dialog
        onOpenChange={(open) => {
          if (!open) {
            setAdding(false);
            setEditing(null);
          }
        }}
        open={adding || editing !== null}
      >
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit vendor" : "Add vendor"}</DialogTitle>
          </DialogHeader>
          <TherapyVendorForm
            defaultValues={editing ?? undefined}
            key={editing?.id ?? "new"}
            onSubmit={(values) =>
              save.mutate(editing ? { ...values, vendorId: editing.id } : values)
            }
            pending={save.isPending}
            submitLabel={editing ? "Save vendor" : "Add vendor"}
            onCancel={() => {
              setAdding(false);
              setEditing(null);
            }}
          />
        </DialogContent>
      </Dialog>

      <AlertDialog open={retiring !== null} onOpenChange={(next) => !next && setRetiring(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Switch off “{retiring?.legalName}”?</AlertDialogTitle>
            <AlertDialogDescription>
              It stops being offered on new notes. Notes already billed keep naming it, which is why
              it is switched off rather than deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep vendor</AlertDialogCancel>
            <AlertDialogAction
              disabled={retire.isPending}
              onClick={() => retiring && retire.mutate({ vendorId: retiring.id })}
            >
              Switch off vendor
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
